import { handleApiError, handleApiResponse } from "@/methods/api";
import ApiVerified from "@/services/apiVerified";
import { AffiliationCourseBatch, PdfReportDisplay } from "@/types/affiliation";
import { 
  AffiliationConnectDisplay, 
  AffiliationConnectInputs, 
  AffiliationMonthlyMeetingDisplay, 
  AffiliationMonthlyMeetingInputs, 
  AffiliationMonthlyMeetingUpdate, 
  AffiliationQueryDisplay, 
  AffiliationQueryInputs, 
  BatchSubjectTeacher, 
  BatchSubjectTeacherDisplay, 
  BatchSubjectTeacherMappings, 
  BulkMapTeachersInputs, 
  InteractionSummaryCard, 
  RescheduleMeetingInputs, 
  UpdateAffiliationConnectInputs, 
  UpdateAffiliationQueryInputs, 
  UpdateMeetingStatusInputs 
} from "@/types/affiliationOps";
import { ApiResponse, IdNamePair } from "@/types/global";

export default class AffiliationOpsApi extends ApiVerified {
  async scheduleAffiliationMonthlyMeeting(
    affiliationMonthlyMeetingInputs: AffiliationMonthlyMeetingInputs,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `affiliation-ops/schedule-affiliation-monthly-meeting`,
        affiliationMonthlyMeetingInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateAffiliationMonthlyMeeting(
    updateAffiliationMeetings: AffiliationMonthlyMeetingUpdate,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `affiliation-ops/update-affiliation-monthly-meeting`,
        updateAffiliationMeetings,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateAffiliationMonthlyMeetingStatus(
    updateMeetingStatusInputs: UpdateMeetingStatusInputs,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `affiliation-ops/update-affiliation-monthly-meeting-status`,
        updateMeetingStatusInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async rescheduleAffiliationMonthlyMeeting(
   rescheduleMeetingInputs: RescheduleMeetingInputs
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `affiliation-ops/reschedule-affiliation-monthly-meeting`,
        rescheduleMeetingInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAffiliationMonthlyMeetingsForAem(
    meetingCategoryId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<AffiliationMonthlyMeetingDisplay>>(
        `affiliation-ops/get-affiliation-monthly-meetings-for-aem`,
        { params: { meetingCategoryId: meetingCategoryId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAffiliationMonthlyMeetingsByAffiliation(affiliationId: number): Promise<ApiResponse> {
      try {
        const response = await this.axiosInstance.get<Array<AffiliationMonthlyMeetingDisplay>>(
          `affiliation-ops/get-affiliation-monthly-meetings-by-affiliation`,
          { params: { affiliationId: affiliationId } },
        );
        return handleApiResponse(response);
      } catch (error) {
        return handleApiError(error);
      }
    }

  async getAffiliationConnectsForAffiliation(affiliationId: number): Promise<ApiResponse> {
      try {
        const response = await this.axiosInstance.get<Array<AffiliationConnectDisplay>>(
          `affiliation-ops/get-affiliation-connects-for-affiliation`,
          { params: { affiliationId: affiliationId } },
        );
        return handleApiResponse(response);
      } catch (error) {
        return handleApiError(error);
      }
    }

  async getAffiliationConnectsForUser(): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<AffiliationConnectDisplay>>(
        `affiliation-ops/get-affiliation-connects-for-user`,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async saveAffiliationConnect(connectData: AffiliationConnectInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<AffiliationConnectDisplay>(
        `affiliation-ops/save-affiliation-connect`,
        connectData,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateAffiliationConnect(connectData: UpdateAffiliationConnectInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<AffiliationConnectDisplay>(
        `affiliation-ops/update-affiliation-connect`,
        connectData,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async saveAffiliationQuery(affiliationQueryInputs: AffiliationQueryInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `affiliation-ops/save-affiliation-query`,
        affiliationQueryInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }
    
  async updateAffiliationQuery(updateAffiliationQueryInputs: UpdateAffiliationQueryInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `affiliation-ops/update-affiliation-query`,
        updateAffiliationQueryInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }
    
  async getAffiliationQueriesForAffiliation(affiliationId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<AffiliationQueryDisplay>>(
        `affiliation-ops/get-affiliation-queries-for-affiliation`,
        { params: { affiliationId: affiliationId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }
    
  async getAffiliationQueriesForOwner(ownerId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<AffiliationQueryDisplay>>(
        `affiliation-ops/get-affiliation-queries-for-owner`,
        { params: { ownerId: ownerId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAffiliationInteractionSummaryCards(affiliationId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<InteractionSummaryCard>>(
        `affiliation-ops/get-affiliation-interaction-summary-cards`,
        { params: { affiliationId: affiliationId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getCourseBatchTreeForAem(): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<AffiliationCourseBatch>>(
        `affiliation-ops/get-course-batch-tree-for-aem`,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getPdfReportsForAffiliation(affiliationId: number, reportType: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<PdfReportDisplay>>(
        `affiliation-ops/get-pdf-reports-for-affiliation`,
        { params: { affiliationId: affiliationId, reportType: reportType } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getBatchTeacherMappingsForAffiliation(affiliationId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<BatchSubjectTeacherDisplay>>(
        `affiliation-ops/get-batch-teacher-mappings-for-affiliation`,
        { params: { affiliationId: affiliationId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async addMultipleBatchSubjectTeacherMappings(
    mappingInputs: BatchSubjectTeacherMappings,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `affiliation-ops/add-multiple-batch-subject-teacher-mappings`,
        mappingInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getTeachersForAffiliation(affiliationId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `affiliation-ops/get-teachers-for-affiliation`,
        { params: { affiliationId: affiliationId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async mapTeacherToBatchSubject(
    batchSubjectTeacherInputs: BatchSubjectTeacher
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post(
        `affiliation-ops/add-batch-subject-teacher-mapping`,
        batchSubjectTeacherInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async bulkMapTeachersToBatches(bulkMapTeachersInputs: BulkMapTeachersInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `affiliation-ops/bulk-map-teachers-to-batches`,
        bulkMapTeachersInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }
}
